// Retry/dedup guard for loading a user's organizations-with-modules.
//
// Extracted out of AuthContext.tsx (2026-10-01 incident: a single tab made
// ~1,800 calls to GET /api/users/{email}/organizations-with-modules in
// ~40 minutes) so the guard logic itself is directly unit-testable without
// rendering the full AuthProvider (Firebase, Sentry, UserSessionRegistry,
// localStorage side effects, 1800+ lines).
//
// Two bugs this fixes, both confirmed by reading the pre-fix code:
// 1. The old retry counter was a React useState read inside the SAME
//    synchronous recursive async chain that was supposed to be bounded by
//    it — every recursive call re-read the value captured when that
//    specific closure was created, which could stay 0 for the whole chain
//    against a persistent 401. Module-level Maps here are read/written
//    synchronously within the recursion, so the cap is real.
// 2. loadUserOrganizations (on 401) called forceSyncWithBackend, which
//    ITSELF called loadUserOrganizations again — AND the original caller
//    recursed into itself too. One 401 forked into two independent retry
//    trees. There is exactly one retry call site here (see `return
//    loadOrgsWithGuard(...)` below) — callers must not add a second one.

export interface OrgLoadGuardDeps<T> {
  /** Performs the actual network call. Throwing is how a failure is signaled. */
  fetchOrgs: (email: string) => Promise<T>;
  /** Attempts to re-establish the session. Must NOT itself reload orgs. */
  resync: () => Promise<boolean>;
  /** Returned when retries are exhausted or the error isn't a 401 — matches the pre-fix contract of never throwing. */
  fallback: T;
  /** Injectable for tests — defaults to a real setTimeout-based sleep. */
  sleep?: (ms: number) => Promise<void>;
  /** Hard cap on 401-triggered resync attempts per email. */
  maxRetries?: number;
  /** First backoff delay; doubles each subsequent attempt (1s, 2s, 4s...). */
  baseBackoffMs?: number;
}

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_BACKOFF_MS = 1000;

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Module-level, not per-call state: must survive across the function's own
// recursive calls for the same email, and must be shared across concurrent
// callers (AuthContext's login flow and its cross-tab resync handler can
// both ask for the same email at nearly the same time).
const inFlight = new Map<string, Promise<any>>();
const retryCounts = new Map<string, number>();

/** Test-only: clears all guard state between test cases. */
export function __resetOrgLoadGuardStateForTests(): void {
  inFlight.clear();
  retryCounts.clear();
}

/**
 * Loads organizations for `email`, deduplicating concurrent calls and
 * capping 401-triggered resync retries with real exponential backoff.
 *
 * Only a 401 ever triggers a retry — a 403, a 429, or any other error
 * returns `fallback` immediately, matching the pre-fix contract (a 403 or
 * 429 was never meant to recurse). Keep that `error.message.includes('401')`
 * check narrow if you touch this — widening it is how a 429 from
 * session/exchange would start looping again.
 */
export async function loadOrgsWithGuard<T = any[]>(
  email: string,
  deps: OrgLoadGuardDeps<T>,
): Promise<T> {
  const existing = inFlight.get(email);
  if (existing) {
    return existing as Promise<T>;
  }

  const {
    fetchOrgs,
    resync,
    fallback,
    sleep = defaultSleep,
    maxRetries = DEFAULT_MAX_RETRIES,
    baseBackoffMs = DEFAULT_BASE_BACKOFF_MS,
  } = deps;

  const promise = (async (): Promise<T> => {
    try {
      try {
        const orgs = await fetchOrgs(email);
        retryCounts.delete(email);
        return orgs;
      } catch (error: any) {
        const attempts = retryCounts.get(email) || 0;
        const is401 = typeof error?.message === 'string' && error.message.includes('401');

        if (is401 && attempts < maxRetries) {
          retryCounts.set(email, attempts + 1);

          const backoffMs = baseBackoffMs * Math.pow(2, attempts);
          await sleep(backoffMs);

          const syncSuccess = await resync();
          if (syncSuccess) {
            // Single retry point: delete this email's in-flight promise
            // (ours is about to resolve from the recursive call instead)
            // and recurse — resync() must never recurse into this itself.
            inFlight.delete(email);
            return loadOrgsWithGuard(email, deps);
          }
        }

        retryCounts.delete(email);
        return fallback;
      }
    } finally {
      inFlight.delete(email);
    }
  })();

  inFlight.set(email, promise);
  return promise;
}
